import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  identificador,
  numero,
  moneda,
  fecha,
  centro,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF del Reporte Costos Por Insumo.
 *
 * Una sola tabla, armada una vez, que alimenta los dos archivos para que no
 * puedan decir cosas distintas (mismo patron que
 * `ReporteCostoVsUltimaCompra/exportarCostoVsUltimaCompra.js`).
 *
 * El orden de las columnas y sus titulos siguen el layout que entrego el
 * cliente. No reordenarlas sin avisar: el archivo se cruza contra su propia
 * plantilla de listas de precios.
 */

const COLUMNAS = [
  // --- Identificacion (catalogo) ---
  { titulo: "Codigo", tipo: "identificador", ancho: 12, enPdf: false },
  { titulo: "Clave", tipo: "identificador", ancho: 18, anchoPdf: 54 },
  { titulo: "Descripcion", tipo: "texto", ancho: 42, anchoPdf: 140 },
  { titulo: "Marca", tipo: "texto", ancho: 18, anchoPdf: 70 },
  { titulo: "Familia", tipo: "texto", ancho: 18, enPdf: false },
  { titulo: "Unidad De Inventario", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Existencia Actual", tipo: "numero", ancho: 13, enPdf: false },

  // --- Costo del catalogo ---
  { titulo: "Costo De Catalogo", tipo: "moneda", ancho: 16, anchoPdf: 66 },
  { titulo: "Moneda Del Insumo", tipo: "texto", ancho: 11, enPdf: false },
  { titulo: "Fecha Actualizacion Costo", tipo: "fecha", ancho: 15, enPdf: false },
  { titulo: "Usuario Que Actualizo", tipo: "texto", ancho: 26, enPdf: false },

  // --- La ultima compra ---
  { titulo: "Numero De Orden De Compra", tipo: "texto", ancho: 20, anchoPdf: 66 },
  { titulo: "Fecha De La Recepcion", tipo: "fecha", ancho: 15, anchoPdf: 56 },
  { titulo: "Dias Desde La Ultima Compra", tipo: "numero", ancho: 11, enPdf: false },
  { titulo: "Proveedor", tipo: "texto", ancho: 32, anchoPdf: 96 },
  { titulo: "Cantidad Comprada", tipo: "numero", ancho: 13, enPdf: false },
  { titulo: "Unidad De Compra", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Factor De Conversion", tipo: "numero", ancho: 13, enPdf: false },

  // --- Moneda y tipo de cambio ---
  { titulo: "Costo Unitario Moneda Original", tipo: "moneda", ancho: 18, enPdf: false },
  { titulo: "Moneda De La Compra", tipo: "texto", ancho: 11, enPdf: false },
  { titulo: "Tipo De Cambio Aplicado", tipo: "numero", ancho: 13, enPdf: false },

  // --- El costo que sirve para fijar precio ---
  { titulo: "Costo Unitario En Pesos", tipo: "moneda", ancho: 18, anchoPdf: 66 },
  { titulo: "Costo Por Unidad De Inventario En Pesos", tipo: "moneda", ancho: 20, anchoPdf: 66 },
  { titulo: "Incluye Gastos De Importacion", tipo: "texto", ancho: 12, enPdf: false },
  { titulo: "Costo Con Gastos De Importacion", tipo: "moneda", ancho: 18, enPdf: false },
  { titulo: "Costo Promedio Actual", tipo: "moneda", ancho: 16, enPdf: false },

  // --- Tendencia y desviacion ---
  { titulo: "Penultima Orden De Compra", tipo: "texto", ancho: 20, enPdf: false },
  { titulo: "Costo Penultima Compra", tipo: "moneda", ancho: 16, enPdf: false },
  { titulo: "Fecha Penultima Compra", tipo: "fecha", ancho: 15, enPdf: false },
  { titulo: "Diferencia Vs Catalogo", tipo: "moneda", ancho: 16, anchoPdf: 62 },
  { titulo: "Diferencia %", tipo: "numero", ancho: 12, anchoPdf: 46 },
  { titulo: "Desviacion", tipo: "texto", ancho: 24, enPdf: false },
];

/** Los mismos umbrales que pintan el semaforo de la tabla en pantalla. */
export const UMBRAL_ALTO = 15;
export const UMBRAL_MEDIO = 5;

export const etiquetaDesviacion = (registro) => {
  if (!registro.fechaUltimaCompra) return "Sin compra";
  if (registro.diferenciaPct === null || registro.diferenciaPct === undefined) {
    return "Sin costo en catalogo";
  }
  if (registro.diferencia > 0) return "Compramos mas caro";
  if (registro.diferencia < 0) return "Compramos mas barato";
  return "Alineado";
};

// El PDF si acepta un color por celda (`reporteFiscalPdf` lo respeta); el Excel
// no, y ahi el sentido lo carga la columna "Desviacion".
const rellenoPdf = (registro) => {
  if (!registro.fechaUltimaCompra) return "#F0F0F0";
  if (registro.diferenciaPct === null || registro.diferenciaPct === undefined) {
    return "#FFCCC7";
  }
  const magnitud = Math.abs(registro.diferenciaPct);
  if (magnitud >= UMBRAL_ALTO) return "#FFCCC7";
  if (magnitud >= UMBRAL_MEDIO) return "#FFE7BA";
  return null;
};

export const construirTabla = (registros) => {
  const filas = (registros || []).map((registro) => {
    const celdaPct =
      registro.diferenciaPct === null || registro.diferenciaPct === undefined
        ? centro("-")
        : numero(registro.diferenciaPct);
    const relleno = rellenoPdf(registro);
    const conRelleno = (celda) => (relleno ? { ...celda, fillColor: relleno } : celda);

    return [
      identificador(registro.codigoInsumo),
      identificador(registro.claveInsumo),
      texto(registro.descripcionInsumo),
      texto(registro.nombreMarca),
      texto(registro.nombreFamilia),
      texto(registro.nombreUnidad),
      numero(registro.existencia),

      moneda(registro.costoCatalogo),
      texto(registro.abreviacionMoneda),
      fecha(registro.fechaCostoCatalogo),
      texto(registro.usuarioCostoCatalogo),

      texto(registro.documento),
      fecha(registro.fechaUltimaCompra),
      numero(registro.diasDesdeUltimaCompra),
      texto(registro.proveedor),
      numero(registro.cantidadComprada),
      texto(registro.unidadCompra),
      numero(registro.factorConversion),

      moneda(registro.costoMonedaOriginal),
      texto(registro.monedaCompra),
      numero(registro.tipoDeCambio),

      moneda(registro.costoUnitarioPesos),
      moneda(registro.costoUnidadInventarioPesos),
      texto(registro.incluyeGastosImportacion),
      moneda(registro.costoConGastosImportacion),
      moneda(registro.costoPromedio),

      texto(registro.documentoPenultima),
      moneda(registro.costoPenultima),
      fecha(registro.fechaPenultimaCompra),

      conRelleno(moneda(registro.diferencia)),
      conRelleno(celdaPct),
      texto(etiquetaDesviacion(registro)),
    ];
  });

  // Sin `totalizar`: aqui todo el dinero es COSTO UNITARIO, y una fila de
  // totales sumaria costos unitarios de insumos distintos, que no significa
  // nada. Con los totales en null, `construirHojaTabla` omite la fila.
  return { columnas: COLUMNAS, filas, totales: COLUMNAS.map(() => null) };
};

const TITULO = "Costos por insumo: catalogo contra la compra real";

const nombreArchivo = (empresaNombre) =>
  `Costos_Por_Insumo_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

const indicadoresDe = (resumen = {}) => [
  { etiqueta: "Insumos en el catalogo", valor: String(resumen.insumosTotales || 0) },
  { etiqueta: "Mostrados", valor: String(resumen.insumosMostrados || 0) },
  { etiqueta: "Sin compra", valor: String(resumen.sinCompra || 0) },
  { etiqueta: "Compramos mas caro", valor: String(resumen.comprandoMasCaro || 0) },
  { etiqueta: "Compramos mas barato", valor: String(resumen.comprandoMasBarato || 0) },
  { etiqueta: "Desviacion promedio", valor: `${resumen.desviacionPromedio || 0}%` },
];

export const descargarExcel = ({ datos, empresaNombre, descripcionFiltros }) => {
  descargarLibro(
    [
      {
        nombre: "Costos Por Insumo",
        hoja: construirHojaTabla(construirTabla(datos), {
          titulo: TITULO,
          empresaNombre,
          descripcionFiltros,
        }),
      },
    ],
    nombreArchivo(empresaNombre)
  );
};

export const descargarPdf = ({ datos, resumen, empresaNombre, descripcionFiltros }) => {
  const tabla = construirTabla(datos);

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: "Reporte Costos Por Insumo",
        empresaNombre,
        descripcionFiltros,
        indicadores: indicadoresDe(resumen),
        contador: tabla.filas.length,
        secciones: [{ titulo: TITULO, ...paraPdf(tabla) }],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
